import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import {
  buildUserSourcesQuery,
  buildUserSourcesByIdsQuery,
  fetchFolderSourceIds,
  rpcGetFolderSourcesPaginated,
  fetchSourceByIdForUser,
  fetchFolderLinksForSource,
  fetchFoldersByIds,
  fetchExtractionStatus,
  fetchSourceFilePaths,
  uploadSourceFile,
  insertSource,
  insertFolderSource,
  updateSource,
  fetchSourceFilePath,
  removeSourceFiles,
  deleteFolderSourcesBySourceId,
  deleteSource,
  deleteFolderSource,
  deleteAllFolderSourceLinks,
  markSourceAsDuplicate,
  batchDeleteFolderSources,
  batchDeleteSources,
  batchUpsertFolderSources,
} from '@/lib/queries/sources';
import type { Source } from '@/types/database';
import type { SourceFilters, SourceUploadInput, SourceWithFolders } from '@/features/sources/types';

const SOURCES_KEY = 'sources';
const SOURCE_KEY = 'source';

function defaultFilters(): SourceFilters {
  return {
    folder_id: null,
    extraction_status: null,
    search: '',
    has_duplicates: null,
    sort_by: 'created_at',
    sort_order: 'desc',
  };
}

const PAGE_SIZE = 50;

/** Escape special PostgREST filter characters */
function escapePostgREST(value: string): string {
  return value.replace(/[%_\\(),.]/g, (c) => `\\${c}`);
}

type SourcesQuery = ReturnType<typeof buildUserSourcesQuery>;

/** Apply common filters to a sources query */
function applyFilters(
  query: SourcesQuery,
  merged: SourceFilters,
) {
  if (merged.extraction_status) {
    query = query.eq('extraction_status', merged.extraction_status);
  }

  if (merged.search) {
    const escaped = escapePostgREST(merged.search);
    query = query.or(
      `title.ilike.%${escaped}%,file_name.ilike.%${escaped}%`
    );
  }

  if (merged.author) {
    query = query.contains('authors', [merged.author]);
  }

  if (merged.doi) {
    const escapedDoi = escapePostgREST(merged.doi);
    query = query.ilike('doi', `%${escapedDoi}%`);
  }

  if (merged.journal) {
    const escapedJournal = escapePostgREST(merged.journal);
    query = query.ilike('journal', `%${escapedJournal}%`);
  }

  if (merged.year_from) {
    query = query.gte('publication_year', merged.year_from);
  }

  if (merged.year_to) {
    query = query.lte('publication_year', merged.year_to);
  }

  if (merged.has_duplicates === true) {
    query = query.eq('is_duplicate', true);
  } else {
    query = query.or('is_duplicate.is.null,is_duplicate.eq.false');
  }

  query = query.order(merged.sort_by, { ascending: merged.sort_order === 'asc' });

  return query;
}

/** Fetch sources with optional filters */
export function useSources(filters: Partial<SourceFilters> = {}) {
  const user = useAuthStore((s) => s.user);
  const merged = { ...defaultFilters(), ...filters };

  return useQuery({
    queryKey: [SOURCES_KEY, user?.id, merged],
    queryFn: async (): Promise<Source[]> => {
      if (!user) return [];

      if (merged.folder_id) {
        // Fetch all folder source IDs (paginated to avoid row limit)
        const allFolderIds: string[] = [];
        const FS_PAGE = 1000;
        let offset = 0;
        while (true) {
          const { data: page } = await fetchFolderSourceIds(merged.folder_id, offset, FS_PAGE);
          if (!page || page.length === 0) break;
          allFolderIds.push(...page.map((fs) => fs.source_id));
          if (page.length < FS_PAGE) break;
          offset += FS_PAGE;
        }
        if (allFolderIds.length === 0) return [];

        // Chunk .in() to stay within PostgREST URL limits
        const CHUNK = 300;
        const allResults: Source[] = [];
        for (let i = 0; i < allFolderIds.length; i += CHUNK) {
          const chunk = allFolderIds.slice(i, i + CHUNK);
          let chunkQuery = buildUserSourcesByIdsQuery(user.id, chunk);
          chunkQuery = applyFilters(chunkQuery, merged);
          const { data: chunkData, error: chunkError } = await chunkQuery;
          if (chunkError) throw chunkError;
          if (chunkData) allResults.push(...(chunkData as Source[]));
        }
        return allResults;
      }

      let query = buildUserSourcesQuery(user.id);
      query = applyFilters(query, merged);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Source[];
    },
    enabled: !!user,
  });
}

/** Paginated sources for list view — uses server-side RPC for single round-trip */
export function usePaginatedSources(
  filters: Partial<SourceFilters> & { page?: number } = {}
) {
  const user = useAuthStore((s) => s.user);
  const merged = { ...defaultFilters(), ...filters };
  const page = filters.page ?? 0;

  return useQuery({
    queryKey: [SOURCES_KEY, 'paginated', user?.id, page, merged],
    queryFn: async (): Promise<{ items: Source[]; totalCount: number }> => {
      if (!user) return { items: [], totalCount: 0 };

      const offset = page * PAGE_SIZE;

      const { data, error } = await rpcGetFolderSourcesPaginated({
        p_user_id: user.id,
        p_folder_id: merged.folder_id || null,
        p_search: merged.search || null,
        p_extraction_status: merged.extraction_status || null,
        p_author: merged.author || null,
        p_doi: merged.doi || null,
        p_journal: merged.journal || null,
        p_year_from: merged.year_from || null,
        p_year_to: merged.year_to || null,
        p_has_duplicates: merged.has_duplicates,
        p_sort_by: merged.sort_by,
        p_sort_order: merged.sort_order,
        p_offset: offset,
        p_limit: PAGE_SIZE,
      });

      if (error) throw error;

      const result = data as { items: Source[]; total_count: number };
      return { items: result.items ?? [], totalCount: result.total_count ?? 0 };
    },
    enabled: !!user,
    placeholderData: (prev) => prev,
  });
}

/** Fetch a single source by ID with its folder relationships */
export function useSource(sourceId: string | undefined) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [SOURCE_KEY, sourceId],
    queryFn: async (): Promise<SourceWithFolders | null> => {
      if (!sourceId || !user) return null;

      const { data: source, error } = await fetchSourceByIdForUser(sourceId, user.id);
      if (error) throw error;

      const { data: folderLinks } = await fetchFolderLinksForSource(sourceId);

      let folders: SourceWithFolders['folders'] = [];
      if (folderLinks && folderLinks.length > 0) {
        const folderIds = folderLinks.map((fl) => fl.folder_id);
        const { data: folderData } = await fetchFoldersByIds(folderIds);
        folders = (folderData ?? []) as SourceWithFolders['folders'];
      }

      return { ...(source as Source), folders };
    },
    enabled: !!sourceId && !!user,
  });
}

/** Upload a file to Supabase Storage and create a source record */
export function useUploadSource() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async (input: SourceUploadInput): Promise<Source> => {
      if (!user) throw new Error('Not authenticated');

      const { file, folder_id } = input;
      const filePath = `${user.id}/${Date.now()}-${file.name}`;

      const { error: uploadError } = await uploadSourceFile(filePath, file);
      if (uploadError) throw uploadError;

      const { data: source, error: insertError } = await insertSource({
        user_id: user.id,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        file_path: filePath,
        extraction_status: 'pending',
        metadata: {},
      });

      if (insertError) throw insertError;

      if (folder_id) {
        await insertFolderSource(folder_id, (source as Source).id);
      }

      // Trigger text extraction via VPS API (fire-and-forget)
      api.extractText({ source_id: (source as Source).id }).catch((err) => {
        console.error('[useSources] text extraction failed:', err);
      });

      return source as Source;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      toast.success('Source uploaded successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upload source');
    },
  });
}

/** Update a source record */
export function useUpdateSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Pick<Source, 'title' | 'authors' | 'publication_year' | 'journal' | 'doi' | 'abstract' | 'keywords'>>;
    }): Promise<Source> => {
      const { data, error } = await updateSource(id, { ...updates, updated_at: new Date().toISOString() });

      if (error) throw error;
      return data as Source;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      queryClient.invalidateQueries({ queryKey: [SOURCE_KEY, data.id] });
      toast.success('Source updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update source');
    },
  });
}

/** Delete a source and its storage file */
export function useDeleteSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sourceId: string): Promise<void> => {
      const { data: source, error: fetchError } = await fetchSourceFilePath(sourceId);
      if (fetchError) throw fetchError;

      if (source?.file_path) {
        await removeSourceFiles([source.file_path]);
      }

      await deleteFolderSourcesBySourceId(sourceId);

      const { error: deleteError } = await deleteSource(sourceId);
      if (deleteError) throw deleteError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      toast.success('Source deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete source');
    },
  });
}

/** Add a source to a folder */
export function useAddSourceToFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceId, folderId }: { sourceId: string; folderId: string }) => {
      const { error } = await insertFolderSource(folderId, sourceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      toast.success('Source added to folder');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add source to folder');
    },
  });
}

/** Move a source to a folder (removes from all other folders first) */
export function useMoveSourceToFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceId, folderId }: { sourceId: string; folderId: string }) => {
      // Remove from all existing folders
      const { error: deleteError } = await deleteAllFolderSourceLinks(sourceId);
      if (deleteError) throw deleteError;

      // Add to the target folder
      const { error: insertError } = await insertFolderSource(folderId, sourceId);
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      queryClient.invalidateQueries({ queryKey: ['folder-tree'] });
      toast.success('Source moved to folder');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to move source to folder');
    },
  });
}

/** Remove a source from a folder */
export function useRemoveSourceFromFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceId, folderId }: { sourceId: string; folderId: string }) => {
      const { error } = await deleteFolderSource(folderId, sourceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      toast.success('Source removed from folder');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove source from folder');
    },
  });
}

/** Resolve a duplicate match */
export function useResolveDuplicate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deleteId,
      keepId,
      action,
    }: {
      deleteId: string | null;
      keepId: string;
      action: 'keep_original' | 'keep_duplicate' | 'dismiss';
    }): Promise<void> => {
      if (action === 'dismiss' || !deleteId) return;

      const { error } = await markSourceAsDuplicate(deleteId, keepId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      toast.success('Duplicate resolved');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to resolve duplicate');
    },
  });
}

/** Batch delete multiple sources */
export function useBatchDeleteSources() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sourceIds: string[]): Promise<{ deleted: number; failed: number }> => {
      // Batch fetch file_paths
      const { data: sources } = await fetchSourceFilePaths(sourceIds);

      // Batch remove storage files
      const filePaths = (sources ?? [])
        .map((s) => s.file_path)
        .filter((p): p is string => !!p);
      if (filePaths.length > 0) {
        await removeSourceFiles(filePaths);
      }

      // Batch delete folder_sources then sources
      await batchDeleteFolderSources(sourceIds);
      const { error } = await batchDeleteSources(sourceIds);

      if (error) {
        return { deleted: 0, failed: sourceIds.length };
      }
      return { deleted: sourceIds.length, failed: 0 };
    },
    onSuccess: ({ deleted, failed }) => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      queryClient.invalidateQueries({ queryKey: ['folder-tree'] });
      if (deleted > 0) toast.success(`Deleted ${deleted} source${deleted > 1 ? 's' : ''}`);
      if (failed > 0) toast.error(`Failed to delete ${failed} source${failed > 1 ? 's' : ''}`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete sources');
    },
  });
}

/** Batch add multiple sources to a folder */
export function useBatchAddToFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceIds, folderId }: { sourceIds: string[]; folderId: string }) => {
      const rows = sourceIds.map((sourceId) => ({
        folder_id: folderId,
        source_id: sourceId,
      }));

      const { error } = await batchUpsertFolderSources(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      queryClient.invalidateQueries({ queryKey: ['folder-tree'] });
      toast.success('Sources added to folder');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add sources to folder');
    },
  });
}

/** Batch remove multiple sources from a folder */
export function useBatchRemoveFromFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceIds, folderId }: { sourceIds: string[]; folderId: string }) => {
      for (const sourceId of sourceIds) {
        const { error } = await deleteFolderSource(folderId, sourceId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCES_KEY] });
      queryClient.invalidateQueries({ queryKey: ['folder-tree'] });
      toast.success('Sources removed from folder');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove sources from folder');
    },
  });
}

/** Poll extraction status for a source until it's completed or failed */
export function useExtractionStatus(sourceId: string | undefined) {
  return useQuery({
    queryKey: ['extraction-status', sourceId],
    queryFn: async () => {
      if (!sourceId) return null;
      const { data, error } = await fetchExtractionStatus(sourceId);

      if (error) throw error;
      return data as Pick<Source, 'id' | 'extraction_status' | 'extracted_text'>;
    },
    enabled: !!sourceId,
    refetchInterval: (query) => {
      const status = query.state.data?.extraction_status;
      if (status === 'pending' || status === 'processing') return 3000;
      return false;
    },
  });
}
